/**
 * Handles a streaming error from the Quick Edit SSE flow.
 *
 * When the server sends an error (e.g., no-op detection), this function:
 * 1. Disables track-change mode
 * 2. Reverts any streamed tracked changes so the editor returns to its pre-edit state
 * 3. Re-enables the editor for further editing
 *
 * @param {object} tiptap - The tiptap editor instance
 * @param {string} editSessionId - The session ID used to scope tracked changes
 * @param {object} options
 * @param {function} options.captureException - Sentry exception capture
 * @param {string} options.documentId - The document ID for error reporting
 * @returns {{ reverted: boolean }} Whether the revert succeeded
 */
export function handleStreamError(tiptap, editSessionId, { captureException, documentId } = {}) {
  if (!tiptap || tiptap.isDestroyed) {
    return { reverted: false };
  }

  tiptap.commands.setTrackChangeStatus(false);

  let reverted = false;
  try {
    tiptap.commands.rejectChangesBySession(editSessionId);
    reverted = true;
  } catch (e) {
    if (captureException) {
      captureException(e, {
        tags: {
          documentId,
          func: "rejectChangesBySession-onError",
        },
      });
    }
  }

  tiptap.setOptions({ editable: true });

  return { reverted };
}

/**
 * Extracts the user-facing error message from a stream error payload.
 *
 * @param {object|null} errorPayload - The error payload from Auth.streamWithIdToken
 * @returns {string} The error message to display to the user
 */
export function getStreamErrorMessage(errorPayload) {
  if (errorPayload?.message && typeof errorPayload.message === "string" && errorPayload.message.trim()) {
    return errorPayload.message.trim();
  }
  return "An error occurred. Please try again.";
}
